import React, {useContext, useState, useMemo, useRef, useCallback, useEffect} from 'react'
import _ from 'lodash'
import moment from 'moment'
import {ReservationCalendarMainContext} from '../../context/ReservationCalendarMain'
import useReservationEventsReducer, {ActionTypes} from '../../state/reservation/events/reducer'
import {getReservationEvents} from '../../state/reservation/events/actions'
import useCalendarViewContext from '../../context/CalendarView'

const useCalendarEvents = () => {
  const {viewData, shareableViewData} = useCalendarViewContext()
  const {selectedDate, calendarView, realSearch, handleSidebarData} = useContext(
    ReservationCalendarMainContext
  )
  const [events, dispatch] = useReservationEventsReducer()

  const callDelay = useRef(null)
  const eventParamsCache = useRef({
    start_date: _.cloneDeep(selectedDate).startOf(`${calendarView}`),
    end_date: _.cloneDeep(selectedDate).endOf(`${calendarView}`),
    group_field: [],
    entity_action_ids: [],
  })

  const eventParamsCacheForShareable = useRef({
    start_date: _.cloneDeep(selectedDate).startOf(`${calendarView}`),
    end_date: _.cloneDeep(selectedDate).endOf(`${calendarView}`),
    group_field: [],
    entity_action_ids: [],
  })

  const dateRange = useMemo(() => {
    if (
      !_.isEmpty(viewData?.selectedView) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      const startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
      const endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)
      if (calendarView === 'day') {
        endDate.add(1, 'day')
      }
      return {startDate: startDate.valueOf(), endDate: endDate.valueOf()}
    }
    return {}
  }, [calendarView, selectedDate, viewData])
  const {startDate, endDate} = dateRange

  const tryCallApi = useCallback(
    params => {
      if (!!!params.entity_id || params.start_date === null || params.end_date === null) return
      if (callDelay.current) {
        clearTimeout(callDelay.current)
        callDelay.current = null
      }
      callDelay.current = setTimeout(() => {
        getReservationEvents(dispatch, params.entity_id, _.cloneDeep(viewData), params)
      }, 250)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, viewData]
  )

  // shareable view
  const tryCallShareableApi = useCallback(
    params => {
      if (!!!params.entity_id || params.start_date === null || params.end_date === null) return
      if (callDelay.current) {
        clearTimeout(callDelay.current)
        callDelay.current = null
      }
      if (shareableViewData?.is_shareable_view) {
        callDelay.current = setTimeout(() => {
          getReservationEvents(dispatch, params.entity_id, _.cloneDeep(shareableViewData), params)
        }, 250)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, shareableViewData]
  )

  const prepareParamsForApi = () => {
    let groups = []
    let actions = []
    let filters = {}
    let VDisplayFields

    let startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
    let endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)

    if (calendarView === 'day') {
      endDate.add(1, 'day')
    }

    if (!_.isEmpty(viewData.selectedFilteredViews)) {
      let _filters = {}
      viewData.selectedFilteredViews.forEach(filteredView => {
        filteredView.filters.forEach(data => {
          if (Array.isArray(data.values)) {
            _filters[data.key] = [...(_filters[data.key] || []), ...data.values.map(v => v.id)]
          } else {
            _filters[data.key] = [...(_filters[data.key] || []), data.values]
          }
        })
        filters = {...filters, ..._filters}
      })
    }

    const VFilters = viewData?.selectedFilteredViews.reduce((ret, sfv) => {
      ret[sfv.name] = sfv.filters.reduce((retf, f) => {
        retf[f.key] = f?.values?.map(v =>
          typeof v.id === 'string' &&
          typeof v.value === 'string' &&
          v.id.includes('_') &&
          v.value.includes(' ') &&
          v.value.replace(' ', '_') === v.id
            ? v.value
            : v.id
        )
        return retf
      }, {})
      return ret
    }, {})

    viewData?.selectedFilteredViews.forEach(val => {
      if (!_.isEmpty(val.display_fields)) {
        VDisplayFields = val.display_fields
      }
    })

    const VData = !_.isEmpty(viewData?.selectedFilteredViews)
      ? viewData.selectedFilteredViews[0]
      : !_.isEmpty(viewData?.selectedView?.group_view) && viewData.selectedView.group_view

    const displayFiedld = !_.isEmpty(viewData?.selectedFilteredViews)
      ? VDisplayFields
      : VData.display_fields

    if (!_.isEmpty(viewData.selectedFilteredViews)) {
      groups = viewData.selectedFilteredViews.flatMap(view =>
        view.groups
          .filter(group => group.active === true)
          .map(group => ({
            ...group,
            calender_view_color: view._id, // Attach _id from view
          }))
      )
    } else if (!_.isEmpty(viewData?.selectedView?.group_view?.groups)) {
      groups = viewData.selectedView.group_view.groups
        .filter(group => group.active === true)
        .map(group => ({
          ...group,
          calender_view_color: viewData.selectedView._id, // Attach _id from selectedView
        }))
    }

    if (!_.isEmpty(viewData.selectedFilteredViews)) {
      actions = viewData.selectedFilteredViews.flatMap(view =>
        view.actions
          .filter(action => action.active === true)
          .map(action => ({
            ...action,
            calender_view_color: view._id, // ✅ Adding calendar view color to actions
          }))
      )
    } else if (!_.isEmpty(viewData?.selectedView?.group_view?.actions)) {
      actions = viewData.selectedView.group_view.actions
        .filter(action => action.active === true)
        .map(action => ({
          ...action,
          calender_view_color: viewData.selectedView._id, // ✅ Adding calendar view color to actions
        }))
    }

    return (eventParamsCache.current = {
      ...eventParamsCache.current,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      group_field: groups,
      entity_action_ids: actions,
      display_fields: displayFiedld || [],
      multiple_filter_views: VFilters,
      entity_id:
        viewData.selectedFilteredViews[0]?.entity_id ||
        viewData.selectedView?.group_view?.entity_id,
      query: realSearch,
    })
  }

  // shareable view
  const prepareParamsForShareableApi = () => {
    let groups = []
    let startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
    let endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)
    if (calendarView === 'day') {
      endDate.add(1, 'day')
    }
    if (!_.isEmpty(shareableViewData?.selectedView?.group_view?.groups)) {
      groups = shareableViewData?.selectedView?.group_view?.groups.map(data => {
        if (data.active === true) {
          return data
        }
        return null
      })
    }
    let actionData = []
    if (!_.isEmpty(shareableViewData?.selectedView?.group_view?.actions)) {
      actionData = shareableViewData?.selectedView?.group_view?.actions.map(data => {
        if (data.active === true) {
          return data
        }
        return null
      })
    }
    return (eventParamsCacheForShareable.current = {
      ...eventParamsCacheForShareable.current,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      group_field: _.compact(groups),
      entity_action_ids: !_.isEmpty(actionData)
        ? _.compact(actionData)
        : shareableViewData?.selectedView?.group_view?.actions,
      multiple_filter_views: {},
      entity_id: shareableViewData?.selectedView?.group_view?.entity_id,
      query: realSearch,
      is_shareable_view: true,
      field_filters: shareableViewData?.selectedView?.group_view?.filters,
      display_fields: shareableViewData?.selectedView?.group_view?.display_fields,
    })
  }

  useEffect(() => {
    if (calendarView === 'day' || calendarView === 'week' || calendarView === 'month') {
      if (!_.isEmpty(viewData?.selectedView)) {
        dispatch({type: ActionTypes.SET_VIEW, payload: {viewData, reset: true}})
        tryCallApi(prepareParamsForApi())
      } else {
        dispatch({type: ActionTypes.RESET})
      }
    }
    //eslint-disable-next-line
  }, [viewData, tryCallApi, dispatch])

  // shareable view
  useEffect(() => {
    if (
      !_.isEmpty(shareableViewData?.selectedView?.group_view) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      dispatch({type: ActionTypes.SET_VIEW, payload: {shareableViewData, reset: true}})
      tryCallShareableApi(prepareParamsForShareableApi())
    }
    //eslint-disable-next-line
  }, [shareableViewData, tryCallShareableApi, dispatch])

  useEffect(() => {
    if (
      !_.isEmpty(viewData?.selectedView) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      const startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
      const endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)
      if (calendarView === 'day') {
        endDate.add(1, 'day')
      }
      eventParamsCache.current = {
        ...eventParamsCache.current,
        start_date: startDate?.subtract(7, 'days').format('YYYY-MM-DD'),
        end_date: endDate?.add(7, 'days').format('YYYY-MM-DD'),
      }
      tryCallApi(eventParamsCache.current)
    }
  }, [startDate, endDate, tryCallApi, viewData, calendarView, selectedDate])

  //shareable view
  useEffect(() => {
    if (
      !_.isEmpty(shareableViewData?.selectedView) &&
      (calendarView === 'day' || calendarView === 'week' || calendarView === 'month')
    ) {
      const startDate = _.cloneDeep(selectedDate).startOf(`${calendarView}`)
      const endDate = _.cloneDeep(selectedDate).endOf(`${calendarView}`)
      if (calendarView === 'day') {
        endDate.add(1, 'day')
      }
      eventParamsCacheForShareable.current = {
        ...eventParamsCacheForShareable.current,
        start_date: startDate?.subtract(7, 'days').format('YYYY-MM-DD'),
        end_date: endDate?.add(7, 'days').format('YYYY-MM-DD'),
      }
      tryCallShareableApi(eventParamsCacheForShareable.current)
    }
    //eslint-disable-next-line
  }, [calendarView, selectedDate, tryCallShareableApi])

  // return {events: {...events}, selectedDate}
  return {
    events: {...events},
    selectedDate,
    handleSidebarData,
    prepareParamsForApi,
    startDate,
    endDate,
  }
}

export default useCalendarEvents
